import type { PlayerCharacter } from "../types/entity.js";
import type { SRDClass } from "../types/srd.js";
import { MAX_LEVEL, levelUpCharacter } from "../party/levelUp.js";
import type { LevelUpSummary } from "../party/levelUp.js";

/**
 * XP-driven level-up progression (W09).
 *
 * Builds on the pure per-level resolver in `engine/party/levelUp.ts` (C11).
 * `processLevelUps` repeatedly applies one level of growth while the
 * character's XP has crossed the next threshold, returning both the final
 * character and an ordered list of per-level summaries so callers can
 * emit a feed line and open an ASI modal per jump.
 *
 * Thresholds match 5e SRD levels 1–10 — bkcrawl caps at 10 per
 * `specs/world-and-progression.md`.
 */

export const XP_THRESHOLDS: readonly number[] = [
  0,
  300,
  900,
  2700,
  6500,
  14000,
  23000,
  34000,
  48000,
  64000,
];

/** Largest level whose threshold is ≤ `xp`. Clamped to [1, MAX_LEVEL]. */
export function levelFromXp(xp: number): number {
  let level = 1;
  for (let i = 1; i < XP_THRESHOLDS.length; i++) {
    const threshold = XP_THRESHOLDS[i];
    if (threshold === undefined) break;
    if (xp >= threshold) level = i + 1;
    else break;
  }
  return Math.min(MAX_LEVEL, level);
}

/** XP required to reach the next level, or `null` at the cap. */
export function xpToNextLevel(currentLevel: number): number | null {
  if (currentLevel >= MAX_LEVEL) return null;
  return XP_THRESHOLDS[currentLevel] ?? null;
}

export interface ProcessLevelUpsResult {
  readonly character: PlayerCharacter;
  /** One summary per level gained, in order. Empty when no threshold was crossed. */
  readonly summaries: readonly LevelUpSummary[];
}

/**
 * Applies as many level-ups as the character's XP warrants.
 *
 * Stops at `MAX_LEVEL`. No-ops for characters already at cap or below
 * their current level's threshold.
 */
export function processLevelUps(
  character: PlayerCharacter,
  classData: SRDClass,
): ProcessLevelUpsResult {
  const target = levelFromXp(character.xp ?? 0);
  if (target <= character.level) {
    return { character, summaries: [] };
  }

  const summaries: LevelUpSummary[] = [];
  let current = character;
  while (current.level < target && current.level < MAX_LEVEL) {
    const step = levelUpCharacter(current, classData);
    if (!step.leveled) break;
    summaries.push(step.summary);
    current = step.character;
  }

  return { character: current, summaries };
}
